import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Text,
  Badge,
  Input,
  Spinner,
  Button,
  Field,
  Textarea,
} from '@fluentui/react-components';
import {
  AddRegular,
  SearchRegular,
  QrCodeRegular,
  ArrowDownloadRegular,
  CheckmarkCircleRegular,
  CheckboxCheckedRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import {
  PermissionGate,
  SearchableDropdown,
  TablePagination,
  SlidePanel,
} from '../../../components/common';
import {
  ApiClient,
  getErrorMessage,
  newToast,
  fullDate,
  API_URL,
} from '../../../configurations/index.config';
import type { QRAsset, QRBatch, Site, PaginationMeta } from '../../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  filters: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexDirection: 'column',
  },
  filterRow: {
    display: 'flex',
    gap: '12px',
    width: '100%',
  },
  filterDropdown: {
    flex: '1',
    minWidth: '0',
  },
  searchInput: {
    width: '100%',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  tableHeader: {
    backgroundColor: '#faf9f8',
    borderBottom: '1px solid #edebe9',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #edebe9',
    ':hover': {
      backgroundColor: '#f3f2f1',
      cursor: 'pointer',
    },
  },
  td: {
    padding: '12px 16px',
    color: '#323130',
  },
  qrCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  qrImage: {
    width: '48px',
    height: '48px',
    border: '1px solid #edebe9',
    borderRadius: '4px',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 16px',
  },
  emptyStateIcon: {
    fontSize: '48px',
    color: '#c8c6c4',
    marginBottom: '16px',
  },
  selectionBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: '#eff6fc',
    borderRadius: '4px',
    marginBottom: '16px',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  thCheckbox: {
    width: '40px',
  },
});

const QRAssetsFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [qrAssets, setQrAssets] = useState<QRAsset[]>([]);
  const [batches, setBatches] = useState<QRBatch[]>([]);
  const [sites, setSites] = useState<Site[]>([]);

  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedBatch, setSelectedBatch] = useState<string>('all');
  const [selectedSite, setSelectedSite] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [pagination, setPagination] = useState<PaginationMeta>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [batchQuantity, setBatchQuantity] = useState<string>('20');
  const [batchNotes, setBatchNotes] = useState('');

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedQRs, setSelectedQRs] = useState<Set<number>>(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [currentPage, itemsPerPage, selectedStatus, selectedBatch, selectedSite, searchQuery]);

  useEffect(() => {
    fetchBatches();
    fetchSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (selectedStatus !== 'all') {
        params.status = selectedStatus;
      }
      if (selectedBatch !== 'all') {
        params.batch_number = selectedBatch;
      }
      if (selectedSite !== 'all') {
        params.site_id = selectedSite;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await ApiClient.get('/get-qr-assets', { params });

      if (response.data.status === 'success') {
        setQrAssets(response.data.data || []);
        if (response.data.pagination) {
          setPagination({
            currentPage: response.data.pagination.current_page,
            totalPages: response.data.pagination.total_pages,
            totalItems: response.data.pagination.total_items,
            itemsPerPage: response.data.pagination.items_per_page,
            hasNextPage:
              response.data.pagination.current_page < response.data.pagination.total_pages,
            hasPreviousPage: response.data.pagination.current_page > 1,
          });
        }
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const fetchBatches = async () => {
    try {
      const response = await ApiClient.get('/get-qr-batches');
      if (response.data.status === 'success') {
        setBatches(response.data.data || []);
      }
    } catch (error: unknown) {
      console.error('Failed to fetch batches:', error);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await ApiClient.get('/get-sites');
      if (response.data.status === 'success') {
        setSites(response.data.data || []);
      }
    } catch (error: unknown) {
      console.error('Failed to fetch sites:', error);
    }
  };

  const handleCreateBatch = async () => {
    const quantity = parseInt(batchQuantity, 10);
    if (isNaN(quantity) || quantity < 1 || quantity > 50) {
      newToast('error', 'Quantity must be between 1 and 50');
      return;
    }

    setCreateLoading(true);
    try {
      const response = await ApiClient.post('/create-qr-batch', {
        quantity,
        notes: batchNotes || undefined,
      });

      if (response.data.status === 'success') {
        newToast('success', `QR batch created successfully with ${quantity} QR codes`);
        setCreateDialogOpen(false);
        setBatchQuantity('20');
        setBatchNotes('');
        fetchData();
        fetchBatches();
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setCreateLoading(false);
  };

  const handleViewDetails = (qr: QRAsset) => {
    navigate(`/account/qr-asset-details/${qr.id}`);
  };

  const handleAssignQR = (qr: QRAsset) => {
    navigate(`/account/assign-qr/${qr.id}`);
  };

  const handleDownloadQR = (qr: QRAsset) => {
    const imageUrl = `${API_URL}${qr.qr_image_url}`;
    const a = document.createElement('a');
    a.href = imageUrl;
    a.download = `${qr.qr_name}.png`;
    a.click();
  };

  const handleToggleSelection = (qrId: number) => {
    setSelectedQRs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(qrId)) {
        newSet.delete(qrId);
      } else {
        newSet.add(qrId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedQRs.size === qrAssets.length) {
      setSelectedQRs(new Set());
    } else {
      setSelectedQRs(new Set(qrAssets.map(qr => qr.id)));
    }
  };

  const handleCancelSelection = () => {
    setSelectionMode(false);
    setSelectedQRs(new Set());
  };

  const downloadQRWithLabel = async (qr: QRAsset): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const padding = 40;
        canvas.width = img.width;
        canvas.height = img.height + padding;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        // White background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw QR image
        ctx.drawImage(img, 0, 0);

        // Draw label at bottom
        ctx.fillStyle = '#323130';
        ctx.font = 'bold 16px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(qr.qr_name, canvas.width / 2, img.height + 26);

        // Download
        const link = document.createElement('a');
        link.download = `${qr.qr_name}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();

        resolve();
      };
      img.onerror = () => reject(new Error(`Failed to load image for ${qr.qr_name}`));
      img.src = `${API_URL}${qr.qr_image_url}`;
    });
  };

  const handleBulkDownload = async () => {
    const selectedAssets = qrAssets.filter(qr => selectedQRs.has(qr.id));
    if (selectedAssets.length === 0) {
      return;
    }

    setBulkDownloading(true);
    let downloaded = 0;
    let failed = 0;

    for (const qr of selectedAssets) {
      try {
        await downloadQRWithLabel(qr);
        downloaded++;
        // Small delay between downloads to prevent browser blocking
        await new Promise(r => setTimeout(r, 300));
      } catch {
        failed++;
      }
    }

    setBulkDownloading(false);
    if (failed > 0) {
      newToast('warning', `Downloaded ${downloaded} QR codes. ${failed} failed.`);
    } else {
      newToast('success', `Successfully downloaded ${downloaded} QR codes`);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      unassigned: { color: 'warning' as const, text: 'Unassigned' },
      assigned: { color: 'success' as const, text: 'Assigned' },
      damaged: { color: 'danger' as const, text: 'Damaged' },
      retired: { color: 'subtle' as const, text: 'Retired' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.unassigned;
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  return (
    <PermissionGate privilege="qr-assets.view" showDenied>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Text className={styles.pageTitle}>QR Asset Management</Text>
              <Text className={styles.pageSubtitle}>
                Manage QR code inventory, batches, and checkpoint assignments
              </Text>
            </div>
            <PermissionGate privilege="qr-assets.create">
              <Button
                appearance="primary"
                icon={<AddRegular />}
                onClick={() => setCreateDialogOpen(true)}
              >
                Create Batch
              </Button>
            </PermissionGate>
          </div>
        </div>

        <ContentCard>
          <div className={styles.filters}>
            <div className={styles.filterRow}>
              <div className={styles.filterDropdown}>
                <SearchableDropdown
                  placeholder="Filter by Status"
                  selectedValue={selectedStatus}
                  onSelect={setSelectedStatus}
                  options={[
                    { id: 'all', label: 'All Status' },
                    { id: 'unassigned', label: 'Unassigned' },
                    { id: 'assigned', label: 'Assigned' },
                    { id: 'damaged', label: 'Damaged' },
                    { id: 'retired', label: 'Retired' },
                  ]}
                />
              </div>

              <div className={styles.filterDropdown}>
                <SearchableDropdown
                  placeholder="Filter by Batch"
                  selectedValue={selectedBatch}
                  onSelect={setSelectedBatch}
                  options={[
                    { id: 'all', label: 'All Batches' },
                    ...batches.map(batch => ({
                      id: batch.batch_number.toString(),
                      label: `Batch ${batch.batch_number} (${batch.quantity} QRs)`,
                    })),
                  ]}
                />
              </div>

              <div className={styles.filterDropdown}>
                <SearchableDropdown
                  placeholder="Filter by Site"
                  selectedValue={selectedSite}
                  onSelect={setSelectedSite}
                  options={[
                    { id: 'all', label: 'All Sites' },
                    ...sites.map(site => ({
                      id: site.id.toString(),
                      label: site.name,
                    })),
                  ]}
                />
              </div>
            </div>

            <Input
              className={styles.searchInput}
              contentBefore={<SearchRegular />}
              placeholder="Search by QR name..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          {selectionMode && (
            <div className={styles.selectionBar}>
              <Text weight="semibold">
                {selectedQRs.size} of {qrAssets.length} selected
              </Text>
              <Button
                appearance="primary"
                icon={<ArrowDownloadRegular />}
                disabled={selectedQRs.size === 0 || bulkDownloading}
                onClick={handleBulkDownload}
              >
                {bulkDownloading ? 'Downloading...' : `Download Selected (${selectedQRs.size})`}
              </Button>
              <Button appearance="subtle" icon={<DismissRegular />} onClick={handleCancelSelection}>
                Cancel
              </Button>
            </div>
          )}

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
              <Spinner label="Loading QR assets..." />
            </div>
          ) : qrAssets.length === 0 ? (
            <div className={styles.emptyState}>
              <QrCodeRegular className={styles.emptyStateIcon} />
              <Text weight="semibold" style={{ display: 'block', marginBottom: '8px' }}>
                No QR Assets Found
              </Text>
              <Text style={{ color: '#605e5c' }}>
                {searchQuery ||
                selectedStatus !== 'all' ||
                selectedBatch !== 'all' ||
                selectedSite !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Create your first QR batch to get started'}
              </Text>
            </div>
          ) : (
            <>
              <table className={styles.table}>
                <thead className={styles.tableHeader}>
                  <tr>
                    {selectionMode ? (
                      <th className={`${styles.th} ${styles.thCheckbox}`}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={selectedQRs.size === qrAssets.length && qrAssets.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                    ) : (
                      <th className={`${styles.th} ${styles.thCheckbox}`}>
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<CheckboxCheckedRegular />}
                          onClick={() => setSelectionMode(true)}
                          title="Select multiple QRs"
                        />
                      </th>
                    )}
                    <th className={styles.th}>QR Code</th>
                    <th className={styles.th}>Status</th>
                    <th className={styles.th}>Batch</th>
                    <th className={styles.th}>Assignment</th>
                    <th className={styles.th}>Created</th>
                    <th className={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {qrAssets.map(qr => (
                    <tr
                      key={qr.id}
                      className={styles.tr}
                      onClick={() =>
                        selectionMode ? handleToggleSelection(qr.id) : handleViewDetails(qr)
                      }
                    >
                      <td className={styles.td} onClick={e => e.stopPropagation()}>
                        {selectionMode ? (
                          <input
                            type="checkbox"
                            className={styles.checkbox}
                            checked={selectedQRs.has(qr.id)}
                            onChange={() => handleToggleSelection(qr.id)}
                          />
                        ) : (
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<CheckboxCheckedRegular />}
                            onClick={() => {
                              setSelectionMode(true);
                              setSelectedQRs(new Set([qr.id]));
                            }}
                            title="Select this QR"
                          />
                        )}
                      </td>
                      <td className={styles.td}>
                        <div className={styles.qrCell}>
                          <img
                            src={`${API_URL}${qr.qr_image_url}`}
                            alt={qr.qr_name}
                            className={styles.qrImage}
                          />
                          <div>
                            <Text weight="semibold" style={{ display: 'block' }}>
                              {qr.qr_name}
                            </Text>
                            <Text size={200} style={{ color: '#605e5c', fontFamily: 'monospace' }}>
                              {qr.qr_token.substring(0, 16)}...
                            </Text>
                          </div>
                        </div>
                      </td>
                      <td className={styles.td}>{getStatusBadge(qr.status)}</td>
                      <td className={styles.td}>
                        <Text>Batch {qr.batch_number}</Text>
                      </td>
                      <td className={styles.td}>
                        {qr.assigned_checkpoint_name ? (
                          <div>
                            <Text weight="semibold" style={{ display: 'block' }}>
                              {qr.assigned_checkpoint_name}
                            </Text>
                            <Text size={200} style={{ color: '#605e5c' }}>
                              {qr.assigned_site_name}
                            </Text>
                          </div>
                        ) : (
                          <Text style={{ color: '#a19f9d' }}>Not assigned</Text>
                        )}
                      </td>
                      <td className={styles.td}>
                        <Text size={200}>{fullDate(qr.created_at)}</Text>
                      </td>
                      <td className={styles.td}>
                        <div className={styles.actions} onClick={e => e.stopPropagation()}>
                          {qr.status === 'unassigned' && (
                            <PermissionGate privilege="qr-assets.assign">
                              <Button
                                size="small"
                                appearance="subtle"
                                icon={<CheckmarkCircleRegular />}
                                onClick={() => handleAssignQR(qr)}
                              >
                                Assign
                              </Button>
                            </PermissionGate>
                          )}
                          <PermissionGate privilege="qr-assets.download">
                            <Button
                              size="small"
                              appearance="subtle"
                              icon={<ArrowDownloadRegular />}
                              onClick={() => handleDownloadQR(qr)}
                            />
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <TablePagination
                currentPage={pagination.currentPage}
                totalItems={pagination.totalItems}
                itemsPerPage={pagination.itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={value => {
                  setItemsPerPage(value);
                  setCurrentPage(1);
                }}
              />
            </>
          )}
        </ContentCard>

        {/* Create Batch Slide Panel */}
        <SlidePanel
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          title="Create QR Code Batch"
          subtitle="Generate a new batch of QR codes"
          width="33%"
          footer={
            <>
              <Button appearance="secondary" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleCreateBatch} disabled={createLoading}>
                {createLoading ? 'Creating...' : 'Create Batch'}
              </Button>
            </>
          }
        >
          <Field label="Quantity" required hint="Enter number of QR codes (1-50)">
            <Input
              type="number"
              value={batchQuantity}
              onChange={e => setBatchQuantity(e.target.value)}
              min={1}
              max={50}
            />
          </Field>
          <Field label="Notes" hint="Optional batch description">
            <Textarea value={batchNotes} onChange={e => setBatchNotes(e.target.value)} rows={3} />
          </Field>
        </SlidePanel>
      </div>
    </PermissionGate>
  );
};

export default QRAssetsFluentUI;
